import React, { useState, useEffect } from 'react';
import styles from './jiraSettings.module.css';
import { Form, Row, Col, Switch, message, Select, Button } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { get } from '../../utils/fetch';
import { useIntegrationConnection } from './useIntegrationConnection';
import { ConnectPrompt } from './ConnectPrompt';

type OptionType = { value: string; label: string };

const ASANA_STATUS_OPTIONS: OptionType[] = [
  { value: 'complete', label: 'Complete' },
  { value: 'incomplete', label: 'Incomplete' }
];

interface Props {
  projectId: string;
  organizationId: string;
  integration:
    | {
        enabled: boolean;
        id: string;
        settings: {
          workspace: OptionType;
          project: OptionType;
          section: OptionType;
          new_tasks: boolean;
          sync_task_updates: boolean;
          guest_public_feedback_section: OptionType;
          new_comments: boolean;
          sync_asana_comments: boolean;
          sync_asana_status: boolean;
          task_status: OptionType;
          status_mapping: Record<string, string>;
        };
      }
    | undefined;
  onDisconnect: () => void;
}

const AsanaSettings: React.FC<Props> = (props: Props) => {
  const [form] = Form.useForm();
  const { integration, organizationId, projectId, onDisconnect } = props;

  const {
    projectIntegrationId,
    isEnabled,
    getConnectedState,
    saveSettings,
    disconnect: handleDisconnect
  } = useIntegrationConnection({
    organizationId,
    projectId,
    integration,
    integrationCode: 'Asana',
    onDisconnect
  });

  const [selectedWorkspace, setSelectedWorkspace] = useState<
    OptionType | undefined
  >(integration?.settings?.workspace);
  const [selectedProject, setSelectedProject] = useState<
    OptionType | undefined
  >(integration?.settings?.project);
  const [selectedSection, setSelectedSection] = useState<
    OptionType | undefined
  >(integration?.settings?.section);
  const [selectedGuestPublicFeedbackSection, setSelectedGuestPublicFeedbackSection] =
    useState<OptionType | undefined>(
      integration?.settings?.guest_public_feedback_section
    );
  const [selectedStatus, setSelectedStatus] = useState<OptionType | undefined>(
    integration?.settings?.task_status
  );

  const [autoPost, setAutoPost] = useState(
    integration?.settings?.new_tasks ?? false
  );
  const [syncTaskUpdates, setSyncTaskUpdates] = useState(
    integration?.settings?.sync_task_updates ?? true
  );
  const [newComments, setNewComments] = useState(
    integration?.settings?.new_comments ?? false
  );
  const [syncAsanaComments, setSyncAsanaComments] = useState(
    integration?.settings?.sync_asana_comments ?? false
  );
  const [syncAsanaStatus, setSyncAsanaStatus] = useState(
    integration?.settings?.sync_asana_status ?? false
  );
  const [statusMappings, setStatusMappings] = useState<Record<string, string>>(
    {}
  );

  const [workspaceOptions, setWorkspaceOptions] = useState<OptionType[]>([]);
  const [projectOptions, setProjectOptions] = useState<OptionType[]>([]);
  const [sectionOptions, setSectionOptions] = useState<OptionType[]>([]);
  const [remoteStatusOptions, setRemoteStatusOptions] = useState<OptionType[]>(
    []
  );
  const [isWorkspacesLoading, setIsWorkspacesLoading] = useState(false);
  const [isProjectsLoading, setIsProjectsLoading] = useState(false);
  const [isSectionsLoading, setIsSectionsLoading] = useState(false);
  const [isStatusLoading, setIsStatusLoading] = useState(false);

  const getWorkspaces = async () => {
    if (!integration?.enabled) return;
    setIsWorkspacesLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/remote_projects?id=${integration.id}&site_name=workspaces`
      );
      setWorkspaceOptions(results);
    } catch (error) {
      message.error('Failed to fetch Asana workspaces. Please try again.');
    } finally {
      setIsWorkspacesLoading(false);
    }
  };

  const getProjects = async (workspaceId: string) => {
    if (!integration?.enabled || !workspaceId) {
      setProjectOptions([]);
      return;
    }
    setIsProjectsLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/remote_projects?id=${integration.id}&site_name=${workspaceId}`
      );
      setProjectOptions(results);
    } catch (error) {
      message.error('Failed to fetch Asana projects. Please try again.');
    } finally {
      setIsProjectsLoading(false);
    }
  };

  const getSections = async (asanaProjectId: string) => {
    if (!integration?.enabled || !asanaProjectId) {
      setSectionOptions([]);
      return;
    }
    setIsSectionsLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/remote_projects?id=${integration.id}&site_name=sections:${asanaProjectId}`
      );
      setSectionOptions(results);
    } catch (error) {
      message.error('Failed to fetch Asana sections. Please try again.');
    } finally {
      setIsSectionsLoading(false);
    }
  };

  const getStatusColumns = async (currentProjectId: string) => {
    if (!integration?.enabled) return;
    setIsStatusLoading(true);
    try {
      const results = await get(`/projects/full?id=${currentProjectId}`);
      const columns: OptionType[] = results.columns.map(
        ({ id, name }: { id: string; name: string }) => ({
          value: String(id),
          label: name
        })
      );
      setRemoteStatusOptions(columns);
    } catch (error) {
      message.error('Failed to fetch status columns. Please try again.');
    } finally {
      setIsStatusLoading(false);
    }
  };

  const handleWorkspaceChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    const next = Array.isArray(option) ? option[0] : option;
    setSelectedWorkspace(next);
    setSelectedProject(undefined);
    setSelectedSection(undefined);
    setSelectedGuestPublicFeedbackSection(undefined);
    form.setFieldsValue({
      project: undefined,
      section: undefined,
      guestPublicFeedbackSection: undefined
    });
  };

  const handleProjectChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    const next = Array.isArray(option) ? option[0] : option;
    setSelectedProject(next);
    setSelectedSection(undefined);
    setSelectedGuestPublicFeedbackSection(undefined);
    setStatusMappings({});
    form.setFieldsValue({
      section: undefined,
      guestPublicFeedbackSection: undefined
    });
  };

  const handleSectionChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    const next = Array.isArray(option) ? option[0] : option;
    setSelectedSection(next);
  };

  const handleGuestPublicFeedbackSectionChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    const next = Array.isArray(option) ? option[0] : option;
    setSelectedGuestPublicFeedbackSection(next);
  };

  const handleStatusChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    const next = Array.isArray(option) ? option[0] : option;
    setSelectedStatus(next);
  };

  const handleStatusMappingChange = (
    bugherdColumnId: string,
    asanaStatus?: string
  ) => {
    setStatusMappings({
      ...statusMappings,
      [bugherdColumnId]: asanaStatus || ''
    });
  };

  const asanaStatusOptionsForColumn = (bugherdColumnId: string) => {
    const selectedAsanaStatuses = Object.entries(statusMappings)
      .filter(
        ([mappedBugherdColumnId, asanaStatus]) =>
          mappedBugherdColumnId !== bugherdColumnId && asanaStatus
      )
      .map(([_mappedBugherdColumnId, asanaStatus]) => asanaStatus);

    return ASANA_STATUS_OPTIONS.filter(
      option => !selectedAsanaStatuses.includes(option.value)
    );
  };

  const handleSubmit = () => {
    const validBugherdColumnIds = remoteStatusOptions.map(
      option => option.value
    );
    const hasLoadedOptions = validBugherdColumnIds.length > 0;

    const cleanedStatusMappings = Object.keys(statusMappings).reduce(
      (acc, bugherdColumnId) => {
        const asanaStatus = statusMappings[bugherdColumnId];
        if (!asanaStatus) return acc;
        if (
          hasLoadedOptions &&
          !validBugherdColumnIds.includes(bugherdColumnId)
        )
          return acc;
        acc[asanaStatus] = bugherdColumnId;
        return acc;
      },
      {} as Record<string, string>
    );

    return saveSettings(
      {
        workspace: selectedWorkspace,
        project: selectedProject,
        section: selectedSection || {},
        new_tasks: autoPost,
        sync_task_updates: syncTaskUpdates,
        guest_public_feedback_section:
          selectedGuestPublicFeedbackSection || {},
        new_comments: newComments,
        sync_asana_comments: syncAsanaComments,
        sync_asana_status: syncAsanaStatus,
        status_mapping: cleanedStatusMappings,
        task_status: selectedStatus || {}
      },
      { form }
    );
  };

  useEffect(() => {
    if (integration?.enabled) {
      getConnectedState();
      getWorkspaces();
      getStatusColumns(projectId);
    }
  }, [integration, projectId, organizationId]);

  useEffect(() => {
    if (integration?.settings?.status_mapping) {
      const invertedMappings = Object.entries(
        integration.settings.status_mapping
      ).reduce((acc, [asanaStatus, bugherdColumnId]) => {
        acc[bugherdColumnId] = asanaStatus;
        return acc;
      }, {} as Record<string, string>);
      setStatusMappings(invertedMappings);
    }
  }, [integration]);

  useEffect(() => {
    if (selectedWorkspace?.value) {
      getProjects(selectedWorkspace.value);
    }
  }, [selectedWorkspace?.value]);

  useEffect(() => {
    if (selectedProject?.value) {
      getSections(selectedProject.value);
    }
  }, [selectedProject?.value]);

  const renderStatusMappings = () => {
    return (
      <div className={styles.statusMappings}>
        <Row className={styles.statusMappingRow} gutter={16}>
          <Col span={11}>
            <strong>BugHerd Status</strong>
          </Col>
          <Col span={2}></Col>
          <Col span={11}>
            <strong>Asana Status</strong>
          </Col>
        </Row>
        {remoteStatusOptions.map(bhStatus => (
          <Row
            key={bhStatus.value}
            className={styles.statusMappingRow}
            gutter={16}
            align="middle"
          >
            <Col span={11}>
              <span className={styles.jiraStatusLabel}>{bhStatus.label}</span>
            </Col>
            <Col span={2} style={{ textAlign: 'center' }}>
              <span>&harr;</span>
            </Col>
            <Col span={11}>
              <Select
                className={styles.bugherdColumnSelect}
                placeholder="Select Asana status"
                value={statusMappings[bhStatus.value] || undefined}
                onChange={value =>
                  handleStatusMappingChange(bhStatus.value, value)
                }
                options={asanaStatusOptionsForColumn(bhStatus.value)}
                allowClear
              />
            </Col>
          </Row>
        ))}
      </div>
    );
  };

  const renderIntegrationSettings = () => {
    return (
      <div>
        <Form
          form={form}
          layout="horizontal"
          onFinish={handleSubmit}
          autoComplete="off"
          requiredMark={false}
        >
          <div className={styles.jiraRequired}>
            <div className={styles.requiredBubble}>REQUIRED</div>
            <h3>Connect your Asana project</h3>
            <Row className={styles.jiraEditRow}>
              <Col span={24}>
                <Form.Item
                  name="workspace"
                  label={
                    <span className={styles.requiredLabel}>
                      <span>*</span>Asana Workspace
                    </span>
                  }
                  className={styles.jiraProject}
                  rules={[
                    {
                      required: true,
                      message: 'Please select an Asana workspace.'
                    }
                  ]}
                  initialValue={selectedWorkspace?.value}
                >
                  <Select
                    className={styles.jiraSelect}
                    showSearch
                    placeholder="Select a workspace"
                    optionFilterProp="label"
                    options={workspaceOptions}
                    onChange={handleWorkspaceChange}
                    loading={isWorkspacesLoading}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
            {selectedWorkspace && (
              <Row className={styles.jiraEditRow}>
                <Col span={24}>
                  <Form.Item
                    name="project"
                    label={
                      <span className={styles.requiredLabel}>
                        <span>*</span>Asana Project
                      </span>
                    }
                    className={styles.jiraProject}
                    rules={[
                      {
                        required: true,
                        message: 'Please select an Asana project.'
                      }
                    ]}
                    initialValue={selectedProject?.value}
                  >
                    <Select
                      className={styles.jiraSelect}
                      showSearch
                      placeholder="Select a project"
                      optionFilterProp="label"
                      options={projectOptions}
                      onChange={handleProjectChange}
                      loading={isProjectsLoading}
                      allowClear
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}
            {selectedProject && (
              <Row className={styles.jiraEditRow}>
                <Col span={24}>
                  <Form.Item
                    name="section"
                    label="Asana Section"
                    className={styles.jiraProject}
                    initialValue={selectedSection?.value}
                    tooltip="This is an optional field. New Asana tasks will be created in this section."
                  >
                    <Select
                      className={styles.jiraSelect}
                      showSearch
                      placeholder="Select a section"
                      optionFilterProp="label"
                      options={sectionOptions}
                      onChange={handleSectionChange}
                      loading={isSectionsLoading}
                      allowClear
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}
          </div>

          <div className={styles.jiraWorkflows}>
            <h3>Choose your desired workflow</h3>
            <Row className={styles.jiraEditRow}>
              <Col span={24}>
                <Form.Item
                  label="Automatically post new tasks to Asana"
                  className={styles.autoPost}
                >
                  <Switch checked={autoPost} onChange={e => setAutoPost(e)} />
                </Form.Item>
              </Col>
              {autoPost && (
                <>
                  <Col span={24}>
                    <Form.Item
                      name="statusColumn"
                      label="Create task when status is equal to"
                      className={styles.selectStatus}
                      initialValue={selectedStatus?.value}
                      tooltip="This is an optional field. Select a status to trigger task creation in Asana."
                    >
                      <Select
                        placeholder="Select a status"
                        options={remoteStatusOptions}
                        onChange={handleStatusChange}
                        loading={isStatusLoading}
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                  {selectedProject && (
                    <Col
                      span={24}
                      className={styles.asanaGuestPublicFeedbackSection}
                    >
                      <Form.Item
                        name="guestPublicFeedbackSection"
                        label="Assign Guest or Public Feedback to Section"
                        className={`${styles.selectStatus} ${styles.asanaGuestPublicFeedbackFormItem}`}
                        initialValue={
                          selectedGuestPublicFeedbackSection?.value
                        }
                        tooltip="This is an optional field. Guest or public feedback tasks will be created in this Asana section."
                      >
                        <Select
                          placeholder="Select a section"
                          options={sectionOptions}
                          onChange={handleGuestPublicFeedbackSectionChange}
                          loading={isSectionsLoading}
                          allowClear
                        />
                      </Form.Item>
                    </Col>
                  )}
                </>
              )}
              <Col span={24}>
                <Form.Item label="Automatically sync task detail updates to Asana">
                  <Switch
                    checked={syncTaskUpdates}
                    onChange={setSyncTaskUpdates}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row className={styles.jiraEditRow}>
              <Form.Item
                className={styles.newComments}
                label="Automatically send new BugHerd comments to Asana"
              >
                <Switch
                  checked={newComments}
                  onChange={e => setNewComments(e)}
                />
              </Form.Item>
            </Row>
            <Row className={styles.jiraEditRow}>
              <Form.Item
                className={styles.syncComments}
                label="Sync comments from Asana to BugHerd"
              >
                <Switch
                  checked={syncAsanaComments}
                  onChange={e => setSyncAsanaComments(e)}
                />
              </Form.Item>
            </Row>
            <Row className={styles.jiraEditRow}>
              <Form.Item
                className={styles.syncStatus}
                label="Sync status updates between Asana and BugHerd"
              >
                <Switch
                  checked={syncAsanaStatus}
                  onChange={e => setSyncAsanaStatus(e)}
                  disabled={!selectedProject}
                />
              </Form.Item>
            </Row>
            {syncAsanaStatus && selectedProject && (
              <div className={styles.jiraEditRow}>
                {renderStatusMappings()}
              </div>
            )}
            <Row className={styles.jiraEditRow}>
              <Col span={24}>
                <Form.Item
                  className={styles.syncUsers}
                  label="Sync users between Asana and BugHerd"
                >
                  <a
                    href={`/settings/organizations/${organizationId}/team?manage_asana_users=true&integration_id=${integration?.id}&project_integration_id=${projectIntegrationId}&workspace_id=${selectedWorkspace?.value}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.manageLink}
                  >
                    Manage <ExportOutlined style={{ marginLeft: '5px' }} />
                  </a>
                </Form.Item>
              </Col>
            </Row>
          </div>

          <Row
            className={styles.jiraEditRow}
            justify="space-between"
            align="middle"
          >
            <Col>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  shape="round"
                  htmlType="submit"
                  className={styles.save}
                >
                  Save
                </Button>
              </Form.Item>
            </Col>
            <Col>
              <Button
                type="text"
                shape="round"
                onClick={() => handleDisconnect({ deleteFallback: true })}
                className={styles.disconnectButton}
                danger
              >
                Disconnect
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {isEnabled ? (
        renderIntegrationSettings()
      ) : (
        <ConnectPrompt
          integrationName="asana"
          projectId={projectId}
          organizationId={organizationId}
          type="Asana"
          display="Asana"
          provider="asana"
        />
      )}
    </div>
  );
};

export default AsanaSettings;
